import dayjs from 'dayjs';
import { startCase } from 'lodash';

import { asCalendarDay } from './dates';

const CURRENCY_FORMATTER = new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('ro-RO');

export const formatCurrency = (value: number | null | undefined, currency = 'EUR'): string => {
  if (value == null) return '';
  const symbol = currency === 'EUR' ? '€' : currency;
  return `${CURRENCY_FORMATTER.format(value)} ${symbol}`;
};

/**
 * Renders the calendar day of `value` as `DD.MM.YYYY`.
 *
 * Uses `asCalendarDay` so backend UTC day markers (`00:00:00Z` / `23:59:59Z`,
 * used for booking availability and campaign timelines) show the day the user
 * actually picked instead of a timezone-shifted neighbour. Genuine timestamps
 * keep rendering in local time.
 */
export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  try {
    return asCalendarDay(value).format('DD.MM.YYYY');
  } catch {
    return String(value);
  }
};

export const formatDateTime = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  try {
    return dayjs(value).format('DD.MM.YYYY HH:mm');
  } catch {
    return String(value);
  }
};

export const formatNumber = (value: number | null | undefined): string => {
  if (value == null) return '';
  return NUMBER_FORMATTER.format(value);
};

export const formatNumericInputDisplay = (value: string | number | undefined): string => {
  if (typeof value === 'number') {
    return value.toLocaleString('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  if (typeof value === 'string' && value !== '' && value !== '-') {
    const parsed = parseFloat(value.replaceAll('.', '').replaceAll(',', '.'));
    if (!isNaN(parsed)) {
      return parsed.toLocaleString('ro-RO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }
  }
  return '';
};

export const formatNumericInputLive = (raw: string): string => {
  if (raw === '' || raw === '-') return raw;
  const commaIdx = raw.lastIndexOf(',');
  const intPart = commaIdx >= 0 ? raw.slice(0, commaIdx) : raw;
  const decPart = commaIdx >= 0 ? raw.slice(commaIdx) : '';
  const sign = intPart.startsWith('-') ? '-' : '';
  const digits = intPart.replace(/[^0-9]/g, '');
  if (digits === '') return sign + decPart;
  const formatted = parseInt(digits, 10).toLocaleString('ro-RO');
  return sign + formatted + decPart;
};

export const parseNumericInput = (raw: string): number =>
  parseFloat(raw.replaceAll('.', '').replaceAll(',', '.'));

/**
 * Title-cases a SNAKE_CASE enum value, e.g. `SHOPPING_MALL` -> `Shopping Mall`.
 */
export const capitalizeWords = (value: string): string => startCase(value.toLowerCase());
